import React, { Dispatch, useMemo, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import capitalize from 'lodash-es/capitalize'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ActionList,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  useSidePanel,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { ContactInstrument, InstrumentType, VerificationStatus } from 'types/contacts'
import { contactInstrumentActionAtom, contactInstrumentAtom } from './atoms/contactAtoms'
import { ContactInstrumentDeleteModal } from './components/ContactInstrumentDeleteModal'
import { ContactInstrumentsSidePanel } from './components/ContactInstrumentsSidePanel'

interface ContactInstrumentsListProps {
  contactInstruments: ContactInstrument[]
  setIsContactInstrumentSheetOpen: Dispatch<React.SetStateAction<boolean>>
}

export const ContactInstrumentsList = ({
  contactInstruments,
  setIsContactInstrumentSheetOpen,
}: ContactInstrumentsListProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const [isDeleteInstrumentModalOpen, setIsDeleteInstrumentModalOpen] = useState(false)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const {
    isSidePanelOpen: isInstrumentDetailsOpen,
    sidePanelData: instrumentDetails,
    paging: pageInstruments,
    setIsSidePanelOpen: setIsInstrumentDetailsOpen,
    updateSidePanel: updateInstrumentDetails,
  } = useSidePanel<ContactInstrument>(contactInstruments)

  const onEditClick = (contactInstrument: ContactInstrument) => {
    const isCryptoInstrument = contactInstrument?.type === InstrumentType.CRYPTO

    setContactInstrument(contactInstrument)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      actionId: isCryptoInstrument
        ? ContactInstrumentActionIds.INSTRUMENT
        : ContactInstrumentActionIds.FIAT_INSTRUMENT,
      onClose: () => {
        setIsContactInstrumentSheetOpen(false)
        setContactInstrument(null)
      },
      header: t('contacts.updateCryptoAccount'),
    })
    setIsContactInstrumentSheetOpen(true)
  }

  const onDeleteClick = (contactInstrument: ContactInstrument) => {
    setContactInstrument(contactInstrument)
    setIsDeleteInstrumentModalOpen(true)
  }

  const onDeleteModalClose = () => {
    setIsDeleteInstrumentModalOpen(false)
    setContactInstrument(null)
  }

  const onVerifyClick = () => {
    navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`)
  }

  const onContactInstrumentClick = (instrumentIndex: number) => {
    updateInstrumentDetails(instrumentIndex)
    setIsInstrumentDetailsOpen(true)
  }

  const actionItemListItems = useMemo(() => {
    return contactInstruments?.map((contactInstrument, instrumentIndex) => {
      const { id, description, currency, address, network, verification, type, accountNumber } =
        contactInstrument || {}
      const isCryptoInstrument = type === InstrumentType.CRYPTO

      const isNotVerified =
        verification?.status === VerificationStatus.VERIFICATION_REQUESTED ||
        verification?.status === VerificationStatus.UNVERIFIED

      const headerHighlightLeft = isNotVerified && (
        <Highlight type="error">{t('contacts.notVerified')}</Highlight>
      )

      const actionButton = isNotVerified && (
        <Button onClick={onVerifyClick} variant="ghost" iconElement={<Icon name="ShieldCheck" />}>
          {t('verify')}
        </Button>
      )

      const bodyPre = isCryptoInstrument
        ? `${currency} (${capitalize(network)}) - ${ellipseMiddleText(address, 6, 6)}`
        : `${currency} - ${ellipseMiddleText(accountNumber, 6, 6)}`

      return {
        transparent: false,
        key: id,
        id: id,
        title: description,
        HeaderHighlightLeft: headerHighlightLeft,
        ActionButton: actionButton,
        Avatar: <Avatar type="currency" name={currency?.toLowerCase()} />,
        BodyPre: bodyPre,
        onItemClick: () => onContactInstrumentClick(instrumentIndex),
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              <DropdownMenuItem
                onClick={() => onContactInstrumentClick(instrumentIndex)}
                key={`action-details-${id}`}
                LeadingIcon={<Icon name="Info" />}
              >
                {t('details')}
              </DropdownMenuItem>,
              ...(canEdit && !viewOnly
                ? [
                    <DropdownMenuItem
                      onClick={() => onEditClick(contactInstrument)}
                      key={`action-edit-${id}`}
                      LeadingIcon={<Icon name="SimplePencilCustom" />}
                    >
                      {t('edit')}
                    </DropdownMenuItem>,
                  ]
                : []),
              ...(canEdit && !viewOnly
                ? [
                    <DropdownMenuItem
                      key={`action-delete-${id}`}
                      LeadingIcon={<Icon name="Trash" />}
                      onClick={() => onDeleteClick(contactInstrument)}
                    >
                      {t('delete')}
                    </DropdownMenuItem>,
                  ]
                : []),
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
      }
    })
  }, [contactInstruments, contactInstrumentAction, canEdit, viewOnly])

  return (
    <>
      <ActionList id="contact-instruments-list" items={actionItemListItems} />

      {isDeleteInstrumentModalOpen && (
        <ContactInstrumentDeleteModal onModalClose={onDeleteModalClose} />
      )}

      <ContactInstrumentsSidePanel
        isOpen={isInstrumentDetailsOpen}
        contactInstrument={instrumentDetails}
        pagingInstruments={pageInstruments}
        onClose={() => setIsInstrumentDetailsOpen(false)}
      />
    </>
  )
}
